import {
    Area,
    CartesianGrid,
    ComposedChart,
    Label,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { DataBucket, MetricsData } from './use-metrics';

import { LocalDate } from '@ridereport/localdate';
import { useMemo } from 'react';
import { DateTime } from 'luxon';
import MetricChartTooltip from './MetricChartTooltip';
import { formatTickOnXAxis, formatTickOnYAxis } from '../chart/util';
import { CHART_ANIMATION_DURATION } from '../chart/constants';

const blue50 = '#3b55e6';
const blue10 = '#b1bbf5';

export type ChartDataType =
    | 'tripsPerDay'
    | 'tripDistancePerDay'
    | 'tripsPerVehiclePerDay'
    | 'meanActiveVehiclesPerDay';

interface MetricChartProps {
    chartDataType: ChartDataType;
    metrics: MetricsData[];
    timeRange: string;
}

function MetricChart({ chartDataType, metrics, timeRange }: MetricChartProps) {
    const chartData = useMemo(() => {
        if (chartDataType === 'tripsPerVehiclePerDay') {
            const trips = metrics.map(metric => {
                return metric.tripsPerDay.dataBuckets;
            });
            const vehicles = metrics.map(metric => {
                return metric.meanActiveVehiclesPerDay.dataBuckets;
            });
            return averageAcrossCities(
                aggregateAcrossCities(trips),
                aggregateAcrossCities(vehicles)
            );
        }

        const ungroupedData = metrics.map(metric => {
            return metric[chartDataType].dataBuckets;
        });
        return aggregateAcrossCities(ungroupedData);
    }, [chartDataType, metrics]);

    const totalDays = useMemo(() => {
        if (chartData[0] == null) return 0;
        const startDate = DateTime.fromISO(chartData[0][0]);
        const endDate = DateTime.fromISO(chartData[chartData.length - 1][0]);
        return endDate.diff(startDate).as('days');
    }, [chartData]);

    const yUnit = metrics[0][chartDataType].yUnit;

    const rollingAverageDuration = timeRange == 'all' ? 14 : 7;

    const rollingAverage = useMemo(() => {
        return chartData.map(([date, values], i) => {
            if (values == null) return { values, average: null, date };
            const daysIncluded = chartData
                .slice(Math.max(i - rollingAverageDuration + 1, 0), i + 1)
                .map(([, value]) => value)
                .filter(value => value !== null) as number[];
            const average =
                daysIncluded.reduce((total, value) => total + value, 0) /
                daysIncluded.length;
            return {
                values,
                average,
                date,
            };
        });
    }, [rollingAverageDuration, chartData]);

    return (
        <ResponsiveContainer width="100%" height="100%">
            {rollingAverage.length === 0 ? (
                <div>No Data</div>
            ) : (
                <ComposedChart
                    margin={{ left: 0, right: 0, bottom: 0, top: 0 }}
                    data={rollingAverage}
                    barCategoryGap={0}
                    barGap={0}
                >
                    <XAxis
                        dataKey="date"
                        tickFormatter={formatTickOnXAxis(totalDays)}
                        interval={0}
                        axisLine={false}
                        tickLine={false}
                        style={{
                            fontSize: '0.75em',
                        }}
                    >
                        <Label
                            value="© Ride Report"
                            position="insideTopRight"
                            fill="#667380"
                            dy={-20}
                            style={{
                                fontSize: '0.85em',
                            }}
                        />
                    </XAxis>
                    <YAxis
                        dataKey="values"
                        tickFormatter={formatTickOnYAxis}
                        axisLine={false}
                        tickLine={false}
                        width={46}
                        domain={[0, 'auto']}
                        style={{
                            fontSize: '0.75em',
                        }}
                    />
                    <CartesianGrid stroke="#ebeef0" vertical={false} />
                    <Tooltip
                        animationDuration={CHART_ANIMATION_DURATION}
                        content={
                            <MetricChartTooltip chartDataType={chartDataType} />
                        }
                    />
                    <Line
                        animationDuration={CHART_ANIMATION_DURATION}
                        dataKey="values"
                        stroke={blue10}
                        opacity={0.5}
                        name={yUnit.split('_').join(' ')}
                        dot={false}
                    />
                    <Area
                        animationDuration={CHART_ANIMATION_DURATION}
                        type="monotone"
                        dataKey="average"
                        stroke={blue50}
                        strokeWidth="0.18em"
                        fill="transparent"
                        name={`${rollingAverageDuration}-day rolling avg`}
                    />
                </ComposedChart>
            )}
        </ResponsiveContainer>
    );
}

type ValuesByDate = [date: string, value: number | null][];

// Creates a new array of databuckets by grouping by xValue and summing each yValue. Preserves order of input.
// Assumes that each input array contains the same xValues.
function aggregateAcrossCities(cityData: DataBucket[][]): ValuesByDate {
    // Maps guarantee that keys are iterated in order of
    // insertion. This makes sure that our keys remain
    // in chronological order
    const data = new Map<string, number>();
    for (const city of cityData) {
        for (const point of city) {
            // Add each date to the map, even if all the values are zero
            const oldValue = data.get(point.xValue) ?? 0;
            data.set(point.xValue, oldValue + point.yValue);
        }
    }
    const dates = Array.from(data.keys());
    if (dates.length <= 1) return Array.from(data.entries());
    dates.sort();
    let currentDate = LocalDate.fromDateString(dates[0]);
    const end = LocalDate.fromDateString(dates[dates.length - 1]);
    const result: ValuesByDate = [];
    while (!currentDate.isAfter(end)) {
        const dateString = currentDate.toString();
        result.push([dateString, data.get(dateString) ?? null]);
        currentDate = currentDate.plusDays(1);
    }
    return result;
}

function averageAcrossCities(
    numeratorValues: ValuesByDate,
    denominatorValues: ValuesByDate
): ValuesByDate {
    let numeratorLine = numeratorValues;
    let denominatorLine = denominatorValues;
    // for each day -> divide numerator by denominator to create new line
    const result: ValuesByDate = [];

    // these two lines should always be of the same length, starting on the same day
    // we'll check the first two dates here to ensure they line up.
    let laterDate = DateTime.fromISO(numeratorLine[0][0]);
    if (
        numeratorLine[0][0] !== denominatorLine[0][0] ||
        numeratorLine.length !== denominatorLine.length
    ) {
        const otherDate = DateTime.fromISO(denominatorLine[0][0]);
        // get the later date of the two
        laterDate = laterDate > otherDate ? laterDate : otherDate;

        const numeratorOffset = numeratorLine.findIndex(
            v => v[0] === laterDate.toFormat('yyyy-LL-dd')
        );
        const denominatorOffset = denominatorLine.findIndex(
            v => v[0] === laterDate.toFormat('yyyy-LL-dd')
        );

        numeratorLine = numeratorLine.slice(numeratorOffset);
        denominatorLine = denominatorLine.slice(denominatorOffset);
    }

    for (let i = 0; i < numeratorLine.length; i++) {
        const [date, numerator] = numeratorLine[i];
        const [_, denominator] = denominatorLine[i];
        // Get average or null if it can't be averaged
        let avg =
            numerator != null && denominator != null && denominator > 50
                ? numerator / denominator
                : null;
        // If the numerator is zero then the average should always be zero
        avg = numerator === 0 ? 0 : avg;
        result[i] = [date, avg];
    }

    return result;
}

export default MetricChart;
